// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import SoftwareEncoderWarning from '@app/features/voice/state/SoftwareEncoderWarning';
import {
	classifyVideoDecoderAcceleration,
	isSoftwareVideoImplementation,
} from '@app/features/voice/utils/VideoAccelerationClassification';
import type {VideoCodec} from 'livekit-client';

const logger = new Logger('ScreenShareCodecDiagnostics');
const DECODER_VERIFICATION_DELAY_MS = 5000;
const UNKNOWN_DECODER_IMPLEMENTATION = 'software decoder';
const UNKNOWN_CODEC_LABEL = 'video';

export {isSoftwareVideoImplementation};

interface CodecStatsEntry {
	type: string;
	id: string;
	mimeType?: string;
}

interface InboundVideoStatsEntry {
	type: string;
	kind?: string;
	mediaType?: string;
	codecId?: string;
	packetsReceived?: number;
	bytesReceived?: number;
	framesDecoded?: number;
	framesReceived?: number;
	decoderImplementation?: string;
	powerEfficientDecoder?: boolean;
}

export interface SoftwareVideoDecoderInfo {
	codec: string;
	implementation: string;
	powerEfficientDecoder: boolean | null;
}

export interface StalledVideoDecoderInfo {
	codec: VideoCodec;
	mimeType?: string;
	packetsReceived: number;
	bytesReceived: number;
	framesDecoded: number;
	framesReceived: number | null;
}

function isSoftwareVideoStats(implementation: string | null, powerEfficient: boolean | null): boolean {
	return classifyVideoDecoderAcceleration(implementation, powerEfficient) === 'software';
}

function getCodecLabel(mimeType: string | undefined): string {
	if (!mimeType) return UNKNOWN_CODEC_LABEL;
	return mimeType.replace(/^video\//i, '').toUpperCase();
}

function getVideoCodecFromMimeType(mimeType: string | undefined): VideoCodec | null {
	const lower = mimeType?.toLowerCase();
	if (!lower?.startsWith('video/')) return null;
	const codec = lower.slice('video/'.length);
	if (codec === 'av1' || codec === 'av1x') return 'av1';
	if (codec === 'h265' || codec === 'hevc') return 'h265';
	if (codec === 'h264') return 'h264';
	if (codec === 'vp9') return 'vp9';
	if (codec === 'vp8') return 'vp8';
	return null;
}

function finiteNumber(value: unknown): number | null {
	return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getStatsKind(report: InboundVideoStatsEntry, codecs: Map<string, CodecStatsEntry>): string | undefined {
	if (report.kind || report.mediaType) return report.kind ?? report.mediaType;
	if (!report.codecId) return undefined;
	const codec = codecs.get(report.codecId);
	return codec?.mimeType?.startsWith('video/') ? 'video' : undefined;
}

export function findSoftwareVideoDecoder(stats: RTCStatsReport): SoftwareVideoDecoderInfo | null {
	const codecs = new Map<string, CodecStatsEntry>();
	const reports: Array<InboundVideoStatsEntry> = [];
	for (const raw of stats.values()) {
		const report = raw as CodecStatsEntry & InboundVideoStatsEntry;
		if (report.type === 'codec') {
			codecs.set(report.id, report);
		}
		if (report.type === 'inbound-rtp') {
			reports.push(report);
		}
	}
	for (const report of reports) {
		if (getStatsKind(report, codecs) !== 'video') continue;
		const implementation =
			typeof report.decoderImplementation === 'string' && report.decoderImplementation.length > 0
				? report.decoderImplementation
				: null;
		const powerEfficientDecoder =
			typeof report.powerEfficientDecoder === 'boolean' ? report.powerEfficientDecoder : null;
		if (!isSoftwareVideoStats(implementation, powerEfficientDecoder)) continue;
		return {
			codec: getCodecLabel(report.codecId ? codecs.get(report.codecId)?.mimeType : undefined),
			implementation: implementation ?? UNKNOWN_DECODER_IMPLEMENTATION,
			powerEfficientDecoder,
		};
	}
	return null;
}

export function findStalledVideoDecoder(stats: RTCStatsReport): StalledVideoDecoderInfo | null {
	const codecs = new Map<string, CodecStatsEntry>();
	const reports: Array<InboundVideoStatsEntry> = [];
	for (const raw of stats.values()) {
		const report = raw as CodecStatsEntry & InboundVideoStatsEntry;
		if (report.type === 'codec') {
			codecs.set(report.id, report);
		}
		if (report.type === 'inbound-rtp') {
			reports.push(report);
		}
	}
	for (const report of reports) {
		if (getStatsKind(report, codecs) !== 'video') continue;
		const framesDecoded = finiteNumber(report.framesDecoded);
		if (framesDecoded === null || framesDecoded > 0) continue;
		const packetsReceived = finiteNumber(report.packetsReceived) ?? 0;
		const bytesReceived = finiteNumber(report.bytesReceived) ?? 0;
		const framesReceived = finiteNumber(report.framesReceived);
		const hasVideoPayload = packetsReceived >= 10 || bytesReceived >= 8192 || (framesReceived ?? 0) >= 2;
		if (!hasVideoPayload) continue;
		const mimeType = report.codecId ? codecs.get(report.codecId)?.mimeType : undefined;
		const codec = getVideoCodecFromMimeType(mimeType);
		if (!codec) continue;
		return {
			codec,
			mimeType,
			packetsReceived,
			bytesReceived,
			framesDecoded,
			framesReceived,
		};
	}
	return null;
}

export interface InboundVideoProgress {
	codec: VideoCodec;
	mimeType?: string;
	packetsReceived: number;
	bytesReceived: number;
	framesDecoded: number;
	framesReceived: number | null;
}

/** Progress counters of every inbound video stream, keyed by stats id. */
export function readInboundVideoProgress(stats: RTCStatsReport): Map<string, InboundVideoProgress> {
	const codecs = new Map<string, CodecStatsEntry>();
	const reports: Array<InboundVideoStatsEntry> = [];
	for (const raw of stats.values()) {
		const report = raw as CodecStatsEntry & InboundVideoStatsEntry;
		if (report.type === 'codec') codecs.set(report.id, report);
		if (report.type === 'inbound-rtp') reports.push(report);
	}
	const progress = new Map<string, InboundVideoProgress>();
	for (const report of reports) {
		if (getStatsKind(report, codecs) !== 'video') continue;
		const framesDecoded = finiteNumber(report.framesDecoded);
		if (framesDecoded === null) continue;
		const mimeType = report.codecId ? codecs.get(report.codecId)?.mimeType : undefined;
		const codec = getVideoCodecFromMimeType(mimeType);
		if (!codec) continue;
		progress.set((report as CodecStatsEntry).id, {
			codec,
			mimeType,
			packetsReceived: finiteNumber(report.packetsReceived) ?? 0,
			bytesReceived: finiteNumber(report.bytesReceived) ?? 0,
			framesDecoded,
			framesReceived: finiteNumber(report.framesReceived),
		});
	}
	return progress;
}

// A window in which video keeps arriving but not one more frame decodes. A static screen
// share sends next to nothing, so it never counts; a paused stream sends nothing at all.
const STALL_WINDOW_MIN_PACKETS = 20;

export function isDecodeStalledBetween(previous: InboundVideoProgress, current: InboundVideoProgress): boolean {
	return (
		current.codec === previous.codec &&
		current.packetsReceived - previous.packetsReceived >= STALL_WINDOW_MIN_PACKETS &&
		current.framesDecoded <= previous.framesDecoded
	);
}

const DECODER_MONITOR_INTERVAL_MS = 3000;
const STALLED_WINDOWS_BEFORE_FAILURE = 2;

/**
 * Watches a subscribed screen share for as long as it is watched. It used to check once, five
 * seconds in, and only caught a decoder that never produced a frame; a decoder that showed one
 * frame and then froze, or a stream whose video had not arrived yet at five seconds, was never
 * caught, so the viewer stayed on a frozen picture and the sharer never switched codec.
 * Reports a decode failure once (never decoded after five seconds, or about six seconds of
 * video arriving with no new frame) and stops. Returns a function that stops the monitor.
 */
export function monitorScreenShareDecoder(
	getStats: () => Promise<RTCStatsReport | undefined>,
	onDecodeFailure?: (failure: StalledVideoDecoderInfo) => void,
): () => void {
	let stopped = false;
	let timer: ReturnType<typeof setTimeout> | null = null;
	let previous = new Map<string, InboundVideoProgress>();
	const stalledWindows = new Map<string, number>();
	const stop = (): void => {
		stopped = true;
		if (timer) clearTimeout(timer);
		timer = null;
	};
	const fail = (failure: StalledVideoDecoderInfo): void => {
		logger.warn('Screen share video decode is stalled', failure);
		stop();
		onDecodeFailure?.(failure);
	};
	const firstCheck = async (stats: RTCStatsReport): Promise<boolean> => {
		const neverDecoded = findStalledVideoDecoder(stats);
		if (neverDecoded) {
			fail(neverDecoded);
			return false;
		}
		const decoder = findSoftwareVideoDecoder(stats);
		if (decoder) {
			logger.warn('Screen share is using a software decoder', decoder);
			SoftwareEncoderWarning.triggerDecoderWarning(decoder.codec, decoder.implementation);
		}
		return true;
	};
	const laterCheck = (stats: RTCStatsReport): boolean => {
		const current = readInboundVideoProgress(stats);
		for (const [id, progress] of current) {
			const before = previous.get(id);
			const windows = before && isDecodeStalledBetween(before, progress) ? (stalledWindows.get(id) ?? 0) + 1 : 0;
			stalledWindows.set(id, windows);
			if (windows >= STALLED_WINDOWS_BEFORE_FAILURE) {
				fail({...progress});
				return false;
			}
		}
		previous = current;
		return true;
	};
	const tick = async (first: boolean): Promise<void> => {
		if (stopped) return;
		try {
			const stats = await getStats();
			if (stopped) return;
			if (stats) {
				const keepGoing = first ? await firstCheck(stats) : laterCheck(stats);
				if (!keepGoing) return;
				if (first) previous = readInboundVideoProgress(stats);
			}
		} catch (error) {
			logger.debug('Failed to verify screen share decoder', {error});
		}
		if (!stopped) timer = setTimeout(() => void tick(false), DECODER_MONITOR_INTERVAL_MS);
	};
	timer = setTimeout(() => void tick(true), DECODER_VERIFICATION_DELAY_MS);
	return stop;
}
